import { useMemo } from "react";
import { AvatarStackItem } from "~/pages/manifold-nextgen/components/AvatarStack";
import { buildExpandedAccess } from "~/pages/manifold-nextgen/components/share/AccessDetailsView";
import {
  type AccessLevelOption,
  type ShareCollaborators,
} from "~/pages/manifold-nextgen/components/share/types";

/**
 * The owners of a shared entity, for its overview "Owners" tile. `ownerRole`
 * is whichever access level counts as ownership there (e.g.
 * `DatasetAccessLevel.OWNER`). `undefined` while the fetch is in flight, so
 * the tile can hold the slot instead of claiming there are no owners.
 */
export const useOwners = (
  collaborators: ShareCollaborators | undefined,
  accessLevels: Record<string, AccessLevelOption>,
  ownerRole: string,
  // Resolves a failed fetch to no owners; the loading state never ends otherwise.
  collaboratorsError?: unknown
): AvatarStackItem[] | undefined =>
  useMemo(() => {
    if (!collaborators) {
      return collaboratorsError ? [] : undefined;
    }
    return buildExpandedAccess(collaborators.users, collaborators.groups, accessLevels)
      .filter((person) => person.effectiveRole === ownerRole)
      .map((person) => ({ id: person.email, name: person.label, imageUrl: person.avatarUrl }));
  }, [collaborators, accessLevels, ownerRole, collaboratorsError]);
